
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import InfoBanner from '@/components/shared/suites/InfoBanner.vue';
import SuitesPanel from '@/components/shared/suites/panels/SuitesPanel.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import IconButton from '@/components/shared/suites/IconButton.vue';
import WarningModal from '@/components/shared/suites/WarningModal.vue';
import TextField from '@/components/shared/TextField.vue';
import SelectComponent from '@/components/shared/Select.vue';
import '@/validation-rules';

const namespace: string = 'subsidies';

@Component<SubsidyConfiguration>({
	components: {
		TextField,
		InfoBanner,
		SuitesPanel,
		TextButton,
		IconButton,
		WarningModal,
		SelectComponent,
		ValidationObserver,
		ValidationProvider
	}
})
export default class SubsidyConfiguration extends Vue {
	@Action('createSubsidy', { namespace }) private createSubsidy!: (subsidy: Subsidy) => Promise<void>;
	@Action('updateSubsidy', { namespace }) private updateSubsidy!: (subsidy: Subsidy) => Promise<void>;
	@Action('deleteSubsidy', { namespace }) private deleteSubsidy!: (id: number) => Promise<void>;
	@Getter('getMaxMealsPerCart', { namespace: 'auth' }) private getMaxMealsPerCart!: number | undefined;
	@Getter('getCurrencySymbol', { namespace: 'auth' }) private currencySymbol!: string;
	@Getter('getPriceAffixes', { namespace: 'auth' }) private priceAffixes!: PriceAffixes;
	@Prop({ required: true }) private subsidy!: Subsidy;
	@Prop({ required: true }) private isConfiguringSubsidy!: boolean;
	@Prop({ required: true }) private hasExistingSubsidies!: boolean;
	@Watch('noMaxMealsLimit')
	private onNoMaxMealsLimitChange(newValue: boolean): void {
		if (newValue) {
			this.maxMealsPerDay = null;
		}
	}

	// Constants
	private readonly DISCOUNT_TYPE_AMOUNT: number = 0;
	private readonly DISCOUNT_TYPE_PERCENTAGE: number = 1;

	// Data
	private discountTypeToggle: number = this.DISCOUNT_TYPE_AMOUNT;
	private maxMealsPerDay: number | null = null;
	private savedMaxMealsPerDay: number | null = null;
	private noMaxMealsLimit: boolean = false;
	private isSaving: boolean = false;
	private isDeleting: boolean = false;
	private showDeleteModal: boolean = false;

	private get subsidyTypes(): { value: BaseSubsidy['type']; label: string }[] {
		return [
			{ value: 'discount', label: this.$t('settings.subsidies.subsidy_type_discount') },
			{ value: 'fixed_price', label: this.$t('settings.subsidies.subsidy_type_fixed_price') },
			{ value: 'pay_what_you_can', label: this.$t('settings.subsidies.subsidy_type_pay_what_you_can') }
		];
	}

	private get discountAmountDisplay(): string {
		if (this.subsidy.type === 'discount') {
			const discountSubsidy = this.subsidy as DiscountSubsidy;
			return discountSubsidy.discount_amount !== null ? (+discountSubsidy.discount_amount!).toFixed(2) : (+discountSubsidy.discount_percentage!).toString();
		}
		else {
			return (+(this.subsidy as FixedPriceSubsidy).discount_fixed_price!).toFixed(2);
		}
	}

	private get discountAmountValidationRules(): string {
		if (this.discountTypeToggle === this.DISCOUNT_TYPE_AMOUNT) {
			return 'required|decimal|higherThanZero';
		}
		else {
			return 'required|integer|higherThanZero|max_value:100';
		}
	}

	private get currentMaxMealsDisplay(): number | null {
		return this.savedMaxMealsPerDay;
	}

	// Target users switch getter and setter (codes or all)
	private get targetUsersSwitch(): boolean {
		return this.subsidy.target_users === 'all';
	}

	private set targetUsersSwitch(value: boolean) {
		this.$emit('update-subsidy', { ...this.subsidy, target_users: value ? 'all' : 'codes' });
	}

	// Target items switch getter and setter (meals or all)
	private get targetItemsSwitch(): boolean {
		return (this.subsidy as DiscountSubsidy | FixedPriceSubsidy).target_items === 'all';
	}

	private set targetItemsSwitch(value: boolean) {
		this.$emit('update-subsidy', { ...this.subsidy, target_items: value ? 'all' : 'meals' });
	}

	private get optOutEnabled(): boolean {
		return this.subsidy.opt_out_enabled || false;
	}

	private set optOutEnabled(value: boolean) {
		this.$emit('update-subsidy', { ...this.subsidy, opt_out_enabled: value });
	}

	// Discount amount input getter and setter
	private get discountAmountInput(): number | null {
		const discountSubsidy = this.subsidy as DiscountSubsidy;
		return this.discountTypeToggle === this.DISCOUNT_TYPE_AMOUNT ? discountSubsidy.discount_amount : discountSubsidy.discount_percentage;
	}

	private set discountAmountInput(value: number | null) {
		if (this.discountTypeToggle === this.DISCOUNT_TYPE_AMOUNT) {
			this.$emit('update-subsidy', { ...this.subsidy, discount_amount: value });
		}
		else {
			this.$emit('update-subsidy', { ...this.subsidy, discount_percentage: value });
		}
	}

	private mounted(): void {
		if (this.subsidy.type === 'discount') {
			this.discountTypeToggle = (this.subsidy as DiscountSubsidy).discount_amount ? this.DISCOUNT_TYPE_AMOUNT : this.DISCOUNT_TYPE_PERCENTAGE;
		}

		// Initialize both editing and saved values from restaurant settings
		const existingMaxMeals = this.getMaxMealsPerCart;
		if (existingMaxMeals) {
			this.maxMealsPerDay = existingMaxMeals;
			this.savedMaxMealsPerDay = existingMaxMeals;
			this.noMaxMealsLimit = false;
		}
		else {
			this.maxMealsPerDay = null;
			this.savedMaxMealsPerDay = null;
			this.noMaxMealsLimit = true;
		}
	}

	private updateSubsidyProp(updates: Partial<Subsidy>): void {
		this.$emit('update-subsidy', { ...this.subsidy, ...updates });
	}

	/**
	 * Reset max meals values to original restaurant settings
	 *
	 * @return {void}
	 */
	private resetMaxMealsValues(): void {
		if (this.savedMaxMealsPerDay) {
			this.maxMealsPerDay = this.savedMaxMealsPerDay;
			this.noMaxMealsLimit = false;
		}
		else {
			this.maxMealsPerDay = null;
			this.noMaxMealsLimit = true;
		}
	}

	/**
	 * Handle cancel button click
	 *
	 * @return {void}
	 */
	private onCancel(): void {
		this.resetMaxMealsValues();
		this.$emit('cancel-configuration');
	}

	/**
	 * When subsidy type changes, emit event to parent to handle the change
	 *
	 * @param {BaseSubsidy['type']} newType
	 * @return {void}
	 */
	private onSubsidyTypeChange(newType: BaseSubsidy['type']): void {
		this.$emit('subsidy-type-change', newType);
	}

	/**
	 * Handle discount type toggle change.
	 * If switching to amount, clear percentage; if switching to percentage, clear amount.
	 *
	 * @param {number} newValue
	 * @return {void}
	 */
	private onDiscountTypeChange(newValue: number): void {
		const discountSubsidy = this.subsidy as DiscountSubsidy;
		if (newValue === this.DISCOUNT_TYPE_AMOUNT) {
			discountSubsidy.discount_percentage = null;
		}
		else {
			discountSubsidy.discount_amount = null;
		}
		this.resetValidator();
	}

	/**
	 * Resets the validation observer.
	 *
	 * @return {void}
	 */
	public resetValidator(): void {
		const observer = this.$refs.observer as any;
		if (observer) {
			observer.reset();
		}
	}

	/**
	 * Validates the form.
	 *
	 * @return {Promise<boolean>}
	 */
	public async validateForm(): Promise<boolean> {
		const observer = this.$refs.observer as any;
		if (observer) {
			return await observer.validate();
		}
		return true;
	}

	/**
	 * Converts string amounts to numbers before saving.
	 * This is necessary because some subsidy amounts will be stored as strings while coming from the database
	 * to prevent loss of precision and the input remains untouched.
	 *
	 * @param {Subsidy} subsidy
	 * @return {Subsidy}
	 */
	private convertStringAmountsToNumbers(subsidy: Subsidy): Subsidy {
		if (subsidy.type === 'discount') {
			const discountSubsidy = subsidy as DiscountSubsidy;
			if (typeof discountSubsidy.discount_amount === 'string') {
				discountSubsidy.discount_amount = parseFloat(discountSubsidy.discount_amount);
			}
			if (typeof discountSubsidy.discount_percentage === 'string') {
				discountSubsidy.discount_percentage = parseFloat(discountSubsidy.discount_percentage);
			}
		}
		else if (subsidy.type === 'fixed_price') {
			const fixedPriceSubsidy = subsidy as FixedPriceSubsidy;
			if (typeof fixedPriceSubsidy.discount_fixed_price === 'string') {
				fixedPriceSubsidy.discount_fixed_price = parseFloat(fixedPriceSubsidy.discount_fixed_price);
			}
		}
		return subsidy;
	}

	/**
	 * Deletes the subsidy configuration.
	 * After deletion, emits event to parent to handle post-deletion state.
	 *
	 * @return {Promise<void>}
	 */
	private async deleteConfiguration(): Promise<void> {
		this.isDeleting = true;

		try {
			await this.deleteSubsidy(this.subsidy.id!);

			// Clear max meals fields after deletion
			this.maxMealsPerDay = null;
			this.savedMaxMealsPerDay = null;
			this.noMaxMealsLimit = false;

			// Emit to parent to handle post-delete state
			this.$emit('subsidy-deleted');
		}
		catch (error) {
			this.$emit('show-error', this.$t('settings.subsidies.error_deleting', { errorMessage: error }));
		}
		finally {
			this.isDeleting = false;
			this.showDeleteModal = false;
		}
	}

	/**
	 * Saves the subsidy configuration.
	 * If the subsidy has an ID, it updates; otherwise, it creates a new subsidy.
	 *
	 * @return {Promise<void>}
	 */
	private async saveConfiguration(): Promise<void> {
		this.isSaving = true;

		try {
			const isValid = await this.validateForm();

			if (!isValid) {
				this.isSaving = false;
				return;
			}

			// Convert string amounts to numbers before submission (if applicable)
			const subsidyToSave: Subsidy = this.convertStringAmountsToNumbers({ ...this.subsidy });
			const payload = { ...subsidyToSave, max_meals_per_cart: this.maxMealsPerDay || 0 } as Subsidy;
			this.subsidy.id ? await this.updateSubsidy(payload) : await this.createSubsidy(payload);

			// Update the saved value to reflect the new saved state
			this.savedMaxMealsPerDay = this.maxMealsPerDay;

			// Emit to parent to handle post-save state
			this.$emit('subsidy-saved');
		}
		catch (error) {
			this.$emit('show-error', this.$t(this.subsidy.id ? 'settings.subsidies.error_updating' : 'settings.subsidies.error_creating', { errorMessage: error }));
		}
		finally {
			this.isSaving = false;
		}
	}
}
